/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import { Colors } from '../../../theme/Colors';
import { Typography } from '../../../theme/Typography';
import { Heading, HeadingLevel } from '../../display/Heading';
import { Button } from '../../navigation/Button/Button';
import { BrandButton } from '../../navigation/BrandButton';
import { DarkBackgroundButton } from '../../navigation/DarkBackgroundButton';
import { DeprecatedBgColor, BgColor, TextColor, CTABtnColor } from './types';
import { TextColorMap } from './constants';
import { getCtaBtnColor } from './utils';
import { AnchorTarget } from '../../../ts-types';

type Alignment = 'left' | 'center' | 'right';

export type Props = {
  heading: React.ReactNode;
  subHeading?: React.ReactNode;
  description?: React.ReactNode;
  ctaText?: React.ReactNode;
  ctaHref?: string;
  ctaBtnColor?: CTABtnColor;
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  bgColor: DeprecatedBgColor | BgColor;
  alignment?: Alignment;
  ctaTarget?: AnchorTarget;
};

const Styles = labelStyles('CTATextContent', {
  root: (alignment: Alignment = 'left') => css`
    text-align: ${alignment};
    @media screen and (min-width: 768px) {
      max-width: ${alignment === 'center' ? '100%' : '450px'};
    }
  `,
  heading: (alignment: Alignment, textColor: TextColor) => css`
    ${Typography.pageHeader4}
    color: ${Colors[textColor]};
    @media screen and (min-width: 768px) {
      ${Typography.pageHeader3}
    }
    margin-bottom: ${alignment === 'center' ? '10px' : '20px'};
  `,
  subHeading: (textColor: TextColor) => css`
    ${Typography.pageHeader9}
    color: ${Colors[textColor]};
    margin-bottom: 20px;
  `,
  description: (alignment: Alignment, textColor: TextColor) => css`
    ${Typography.paragraph1ShortBold}
    color: ${Colors[textColor]};
    margin-bottom: ${alignment === 'center' ? '30px' : '20px'};
  `,
  ctaContainer: css`
    & > a,
    & > button {
      width: 100%;
    }

    @media screen and (min-width: 768px) {
      & > a,
      & > button {
        width: auto;
      }
    }
  `,
});

const getTextColor = (bgColor: BgColor | DeprecatedBgColor) => {
  return TextColorMap[bgColor];
};

const getButton = (props: Props) => {
  const btnProps = {
    children: props.ctaText,
    href: props.ctaHref,
    onClick: props.onCtaClick,
    onKeyDown: props.onCtaKeyDown,
    target: props.ctaTarget,
    'data-testid': 'cta-button',
  };
  const btnColor = getCtaBtnColor(props.bgColor, props.ctaBtnColor);
  if (btnColor === 'primary-dark')
    return <DarkBackgroundButton {...btnProps} />;
  else if (btnColor === 'brand') return <BrandButton {...btnProps} />;
  else {
    return <Button {...btnProps} color={btnColor} size="small" />;
  }
};

/**
 * Internal component for text content in `CTAStandard` and `CTAFilled` that
 * shares the same structure and styling.
 */
export const CTATextContent = (props: Props) => {
  const textColor = getTextColor(props.bgColor);

  return (
    <div css={Styles.root(props.alignment)}>
      <HeadingLevel>
        <Heading css={Styles.heading(props.alignment, textColor)}>
          {props.heading}
        </Heading>
        {props.subHeading && (
          <HeadingLevel>
            <Heading css={Styles.subHeading(textColor)}>
              {props.subHeading}
            </Heading>
          </HeadingLevel>
        )}
      </HeadingLevel>
      {props.description && (
        <div css={Styles.description(props.alignment, textColor)}>
          {props.description}
        </div>
      )}
      {props.ctaText && (props.ctaHref || props.onCtaClick) && (
        <div css={Styles.ctaContainer}>{getButton(props)}</div>
      )}
    </div>
  );
};
